import { useState, useEffect } from 'react';
import { useIntl } from 'gatsby-plugin-intl';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';

import { fixPropWarnings } from '../utils/fixPropWarnings';
import useKeyValuePairs from '../hooks/useKeyValuePairs';

interface PaginationProps {
   className?: string;
   totalArticle: number;
   articlesPerPage: number;
   currentPage: number;
   paginate: (page: number) => void;
   color?: string | ReturnType<typeof themeGet>;
   anchor?: string;
   bgColor?: string;
   noScroll?: boolean;
}

interface NavigationProps {
  paginationColor?: string | ReturnType<typeof themeGet>;
  backgroundColor?: string;
}

const Navigation = styled.ul.withConfig(fixPropWarnings('paginationColor'))<NavigationProps>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  margin: 1.5rem;

  button {
    border: 1px solid
      ${({ paginationColor }) => paginationColor || themeGet('colors.border-grey')};
    color: ${({ paginationColor }) => (paginationColor || themeGet('colors.teal'))};
    padding: 0.5rem;
    min-width: 60px;
    min-height: 54px;
    font-size: 12px;
    line-height: normal;
  }

  button.active {
    color: ${themeGet('colors.white')};
    background-color: ${({ paginationColor }) => paginationColor || themeGet('colors.teal')};
  }

  button:disabled {
    color: ${themeGet('colors.secondary-text')};
  }

  button.first {
    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;
    border-right: none;
  }

  button.last {
    border-top-right-radius: 5px;
    border-bottom-right-radius: 5px;
    border-left: none;
  }

  li {
    background: ${({ backgroundColor }) => backgroundColor || themeGet('colors.greys.1')};
  }
`;

const Pagination = ({
  className,
  totalArticle,
  articlesPerPage,
  currentPage,
  paginate,
  color,
  anchor,
  bgColor,
  noScroll,
}: PaginationProps) => {
  const intl = useIntl();
  const keyValuePairs = useKeyValuePairs(intl.locale);

  const lastPage = Math.ceil(totalArticle / articlesPerPage);
  const previousPage = currentPage - 1;
  const nextPage = currentPage + 1;
  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === lastPage;

  const [mobile, setMobile] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const matchMedia = (event?: { matches: any; }) => setMobile(!!event?.matches);
    const mediaQuery = window.matchMedia('(max-width: 415px)');
    mediaQuery.addEventListener('change', matchMedia);
    matchMedia();
    return () => mediaQuery.removeEventListener('change', matchMedia);
  }, []);

  useEffect(() => {
    if (noScroll) return;
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
    if (anchor) {
      document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [anchor, currentPage, noScroll]);

  return (
    <nav
      role="navigation"
      aria-label="Pagination Navigation"
      className={className}
    >
      <Navigation
        paginationColor={color}
        className="no-bullets"
        backgroundColor={bgColor}
      >
        <li>
          <button
            type="button"
            className="first"
            disabled={isFirstPage}
            onClick={() => {
              paginate(1);
            }}
          >
            {keyValuePairs.PAGINATION_FIRST}
          </button>
        </li>
        <li>
          <button
            type="button"
            disabled={isFirstPage}
            onClick={() => {
              paginate(previousPage);
            }}
          >
            {keyValuePairs.PAGINATION_PREVIOUS}
          </button>
        </li>
        <li className={mobile ? 'mobile-hide' : ''}>
          <button type="button" className="active">
            {`${currentPage} ${keyValuePairs.PAGINATION_OF} ${lastPage || 1}`}
          </button>
        </li>
        <li>
          <button
            type="button"
            disabled={isLastPage}
            onClick={() => {
              paginate(nextPage);
            }}
          >
            {keyValuePairs.PAGINATION_NEXT}
          </button>
        </li>
        <li>
          <button
            type="button"
            className="last"
            disabled={isLastPage}
            onClick={() => {
              paginate(lastPage);
            }}
          >
            {keyValuePairs.PAGINATION_LAST}
          </button>
        </li>
      </Navigation>
    </nav>
  );
};

export default Pagination;
